// RolesPage.tsx — admin UI for creating and viewing roles. Each row links to
// the role detail page; permissions are revealed via an expando rather than
// a wall of badges.
import { Fragment, useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { BsPlusLg } from 'react-icons/bs'
import Text from 'common/ui/Text'
import Combobox from 'common/ui/Combobox'
import Expando, { ExpandoChevron } from 'common/ui/Expando'
import NameTag from '../components/NameTag'
import { useAuth, apiFetch } from '../hooks/useAuth'

interface RoleData {
    name: string
    description?: string
    permissions: string[]
}

const RolesPage = () => {
    const navigate = useNavigate()
    const { email, loading, error: authError } = useAuth()
    const [allRoles, setAllRoles] = useState<RoleData[]>([])
    const [newRoleName, setNewRoleName] = useState('')
    const [newRoleDesc, setNewRoleDesc] = useState('')
    const [newRolePerms, setNewRolePerms] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [search, setSearch] = useState('')

    const fetchRoles = useCallback(async () => {
        try {
            const res = await apiFetch('/api/admin/roles')
            if (!res.ok) return
            const data = await res.json()
            setAllRoles(data.roles || [])
        } catch { /* user may lack admin scopes */ }
    }, [])

    useEffect(() => {
        if (!loading && email) fetchRoles()
    }, [loading, email, fetchRoles])

    const filteredRoles = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return allRoles
        return allRoles.filter((r) =>
            r.name.toLowerCase().includes(q)
            || (r.description || '').toLowerCase().includes(q)
            || (r.permissions || []).some((p) => p.toLowerCase().includes(q))
        )
    }, [allRoles, search])

    const handleCreateRole = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setSuccess('')
        const permissions = newRolePerms.split(',').map((s) => s.trim()).filter(Boolean)
        if (!newRoleName) { setError('Role name is required'); return }

        try {
            const res = await apiFetch('/api/admin/roles', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newRoleName, description: newRoleDesc, permissions }),
            })
            if (!res.ok) {
                const data = await res.json()
                throw new Error(data.error || 'Failed to create role')
            }
            setSuccess(`Role "${newRoleName}" created`)
            setNewRoleName('')
            setNewRoleDesc('')
            setNewRolePerms('')
            fetchRoles()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to create role')
        }
    }

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const q = search.trim()
        if (!q) return
        const exact = allRoles.find((r) => r.name === q)
        if (exact) navigate(`/roles/${encodeURIComponent(exact.name)}`)
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <>
            <h1 className="mb-4">Manage Roles</h1>
            {authError && <Alert variant="danger">{authError}</Alert>}

            <Card className="mb-3">
                <Card.Header>Create Role</Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}
                    <Form id="create-role-form" onSubmit={handleCreateRole}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="role.name"
                                value={newRoleName}
                                onChange={(e) => setNewRoleName(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="optional"
                                value={newRoleDesc}
                                onChange={(e) => setNewRoleDesc(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Scopes</Form.Label>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="scope.one, scope.two"
                                value={newRolePerms}
                                onChange={(e) => setNewRolePerms(e.target.value)}
                            />
                            <Form.Text muted>Comma-separated list — add or remove scopes individually from each role's detail page.</Form.Text>
                        </Form.Group>
                    </Form>
                </Card.Body>
                <Card.Footer>
                    <Button variant="success" type="submit" form="create-role-form" className="d-flex align-items-center gap-2">
                        <BsPlusLg /> Create
                    </Button>
                </Card.Footer>
            </Card>

            <Card className="mb-3">
                <Card.Header>
                    Existing Roles ({filteredRoles.length}{search ? ` of ${allRoles.length}` : ''})
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSearchSubmit} className="mb-3">
                        <Form.Group>
                            <Form.Label>Search</Form.Label>
                            <Combobox
                                value={search}
                                onChange={setSearch}
                                options={allRoles.map((r) => r.name)}
                                style={{ width: "100%" }}
                                placeholder="filter by name, description, or scope — Enter on an exact role name to open it"
                            />
                        </Form.Group>
                    </Form>
                    {filteredRoles.length > 0 ? (
                        <Table size="sm" hover responsive className="m-0">
                            <thead>
                                <tr>
                                    <th style={{ width: '1%' }}></th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th style={{ width: '6em' }}>Scopes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRoles.map((role) => {
                                    const count = role.permissions?.length || 0
                                    return (
                                        <Expando key={role.name}>
                                            {({ open, toggle }) => (
                                                <Fragment>
                                                    <tr>
                                                        <td className="align-middle">
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0 text-muted"
                                                                onClick={toggle}
                                                                aria-label={open ? 'Collapse' : 'Expand'}
                                                                aria-expanded={open}
                                                                title={open ? 'Hide scopes' : 'Show scopes'}
                                                                disabled={count === 0}
                                                            >
                                                                {count === 0 ? '·' : <ExpandoChevron open={open} />}
                                                            </Button>
                                                        </td>
                                                        <td>
                                                            <NameTag name={role.name} kind="role" to={`/roles/${encodeURIComponent(role.name)}`} />
                                                        </td>
                                                        <td className="text-muted">{role.description || '—'}</td>
                                                        <td className="text-muted">{count}</td>
                                                    </tr>
                                                    {open && count > 0 && (
                                                        <tr className="bg-body-tertiary">
                                                            <td></td>
                                                            <td colSpan={3}>
                                                                <div className="d-flex flex-wrap column-gap-3 row-gap-1" style={{ paddingLeft: '1.5em' }}>
                                                                    {role.permissions.map((p) => (
                                                                        <NameTag key={p} name={p} kind="scope" to={`/scopes/${encodeURIComponent(p)}`} />
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Expando>
                                    )
                                })}
                            </tbody>
                        </Table>
                    ) : allRoles.length === 0 ? (
                        <span className="text-muted small">No roles defined yet</span>
                    ) : (
                        <span className="text-muted small">No roles match "{search}"</span>
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

export default RolesPage
